import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    Alert,
    Button,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Tabs,
    Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";

import { useAuth } from "../../../../auth";
import { customInstanceDocumentos } from "../../../../api/axios-client";
import type { DoczBffDetailDto } from "../../../../api/documentos/model/doczBffDetailDto";
import type { DoczBffArquivoDto } from "../../../../api/documentos/model/doczBffArquivoDto";
import { useTranslation } from "react-i18next";
import { DocumentHistoryTab } from "../components/DocumentHistoryTab";

const viewSearchSchema = z.object({
    identificador: z.string(),
    from: z.string().optional(),
});

export const Route = createFileRoute("/__auth/documents/docz/view")({
    component: ViewDoczDocumentPage,
    staticData: {
        title: "routes.viewDocument",
    },
    validateSearch: (search) => viewSearchSchema.parse(search),
});

function isViewable(contentType?: string): boolean {
    if (!contentType) return false;
    return contentType === "application/pdf" || contentType.startsWith("image/");
}

function formatDate(dateString?: string): string {
    if (!dateString) return "—";
    try {
        return new Date(dateString).toLocaleDateString("pt-BR", { timeZone: "UTC" });
    } catch {
        return dateString;
    }
}

function getFileIcon(contentType?: string) {
    if (!contentType) return <InsertDriveFileIcon />;
    if (contentType.includes("pdf")) return <PictureAsPdfIcon color="error" />;
    if (contentType.startsWith("image/")) return <ImageIcon color="primary" />;
    if (contentType.includes("word") || contentType.includes("document"))
        return <DescriptionIcon color="info" />;
    return <InsertDriveFileIcon />;
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
    return (
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {label}
            </Typography>
            <Typography variant="body2">
                {value || "—"}
            </Typography>
        </Box>
    );
}

function ViewDoczDocumentPage() {
    const { t } = useTranslation();
    const { currentTenant, user } = useAuth();
    const tenant = currentTenant || user?.tenant || "default-domain";
    const search = Route.useSearch();
    const navigate = useNavigate();
    const identificador = search.identificador;

    const [selectedFile, setSelectedFile] = useState<DoczBffArquivoDto | null>(null);
    const [fileViewerSrc, setFileViewerSrc] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const { data: document, isLoading, error } = useQuery({
        queryKey: ["docz-detail", tenant, identificador],
        queryFn: async (): Promise<DoczBffDetailDto> => {
            const params = new URLSearchParams({
                tenant,
                identificador,
                portalUrl: window.location.hostname,
            });
            return customInstanceDocumentos<DoczBffDetailDto>(
                `/api/v1/bff/docz-documentos/details?${params.toString()}`,
                { method: "GET" }
            );
        },
        enabled: !!tenant && !!identificador,
    });

    useEffect(() => {
        if (document?.arquivos?.length && !selectedFile) {
            setSelectedFile(document.arquivos[0]);
        }
    }, [document, selectedFile]);

    useEffect(() => {
        if (!selectedFile?.id || !document?.idInterno) return;
        if (!isViewable(selectedFile.contentType)) {
            setFileViewerSrc(null);
            return;
        }

        let cancelled = false;
        setFileViewerSrc(null);

        const fetchFile = async () => {
            try {
                const response = await customInstanceDocumentos<Blob>(
                    `/api/v1/bff/docz-documentos/download?tenant=${tenant}&arquivoId=${selectedFile.id}&version=${selectedFile.version || 1}`,
                    { method: "GET", responseType: "blob" } as any
                );

                if (cancelled) return;

                const blob = response instanceof Blob
                    ? response
                    : new Blob([response as any], { type: selectedFile.contentType || "application/pdf" });
                const url = URL.createObjectURL(blob);
                setFileViewerSrc(url);
            } catch (err) {
                console.error("Error downloading file for preview:", err);
            }
        };

        fetchFile();
        return () => { cancelled = true; };
    }, [selectedFile, document?.idInterno, tenant]);

    useEffect(() => {
        return () => {
            if (fileViewerSrc) URL.revokeObjectURL(fileViewerSrc);
        };
    }, [fileViewerSrc]);

    const handleDownload = async () => {
        if (!selectedFile?.id || !document?.idInterno) return;
        setIsDownloading(true);
        try {
            const response = await customInstanceDocumentos<Blob>(
                `/api/v1/bff/docz-documentos/download?tenant=${tenant}&arquivoId=${selectedFile.id}&?version=${selectedFile.version || 1}`,
                { method: "GET", responseType: "blob" } as any
            );
            const blob = response instanceof Blob
                ? response
                : new Blob([response as any], { type: selectedFile.contentType || "application/octet-stream" });
            const url = URL.createObjectURL(blob);
            const a = window.document.createElement("a");
            a.href = url;
            a.download = selectedFile.nomeArquivo || "arquivo";
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading file:", err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleBack = () => {
        if (search.from) {
            navigate({ to: search.from } as any);
        } else {
            window.history.back();
        }
    };

    const metadados = document?.metadados || {};
    const metadataKeys = Object.keys(metadados).filter(
        (k) => !["Deletado", "Identificador SOS"].includes(k)
    );

    const arquivos = document?.arquivos || [];
    const canPreview = selectedFile ? isViewable(selectedFile.contentType) : false;

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    {t("documents.errorLoadingDocument")}
                </Alert>
            </Box>
        );
    }

    return (
        <Stack spacing={3} sx={{ p: 2 }}>
            <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    {(window.history.length > 1 || search.from) && (
                        <IconButton onClick={handleBack} color="primary" size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    {isLoading ? (
                        <Skeleton variant="text" width={350} height={40} />
                    ) : (
                        <Box>
                            <Typography variant="h5">
                                {document?.identificador || identificador}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {document?.tipoDocumento || t("documents.title")}{" "}
                                {document?.situacao && `· ${document.situacao}`}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Paper>

            {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && document && (
                <>
                    <Paper sx={{ p: 2 }}>
                        {arquivos.length > 1 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    {t("documents.files")} ({arquivos.length})
                                </Typography>
                                <List dense disablePadding sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {arquivos.map((f, index) => (
                                        <ListItemButton
                                            key={f.id || index}
                                            selected={selectedFile?.id === f.id}
                                            onClick={() => setSelectedFile(f)}
                                            sx={{
                                                borderRadius: 1,
                                                border: "1px solid",
                                                borderColor: selectedFile?.id === f.id ? "primary.main" : "divider",
                                                maxWidth: 250,
                                                py: 0.5,
                                                px: 1,
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                {getFileIcon(f.contentType)}
                                            </ListItemIcon>
                                            <ListItemText>
                                                <Tooltip title={f.nomeArquivo || ""}>
                                                    <Typography noWrap variant="body2" sx={{ fontSize: 13 }}>
                                                        {f.nomeArquivo || `Arquivo ${index + 1}`}
                                                    </Typography>
                                                </Tooltip>
                                            </ListItemText>
                                        </ListItemButton>
                                    ))}
                                </List>
                                <Divider sx={{ mt: 2 }} />
                            </Box>
                        )}

                        {!selectedFile ? (
                            <Box sx={{ textAlign: "center", py: 6 }}>
                                <Typography color="text.secondary">
                                    {t("documents.noFilesFound")}
                                </Typography>
                            </Box>
                        ) : canPreview ? (
                            fileViewerSrc ? (
                                <>
                                    {selectedFile.contentType?.startsWith("image/") ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                minHeight: 300,
                                                bgcolor: "#f5f5f5",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <img
                                                src={fileViewerSrc}
                                                alt={selectedFile.nomeArquivo || t("common.view")}
                                                style={{
                                                    maxWidth: "100%",
                                                    maxHeight: "70vh",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            component="iframe"
                                            src={fileViewerSrc}
                                            sx={{
                                                border: "none",
                                                width: "100%",
                                                height: "70vh",
                                                minHeight: 600,
                                                display: "block",
                                                borderRadius: 1,
                                            }}
                                            title={t("documents.fileVisualization")}
                                        />
                                    )}
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                        <Button
                                            size="small"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownload}
                                            disabled={isDownloading}
                                        >
                                            {isDownloading ? t("common.downloading") : t("documents.downloadFile")}
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={300}
                                    sx={{ borderRadius: 1 }}
                                />
                            )
                        ) : (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 6,
                                    bgcolor: "action.hover",
                                    borderRadius: 1,
                                }}
                            >
                                <InsertDriveFileIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    {selectedFile.nomeArquivo || t("documents.file")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t("documents.cannotViewFile")}
                                    {selectedFile.contentType && ` (${selectedFile.contentType})`}
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? t("common.downloading") : t("documents.downloadFile")}
                                </Button>
                            </Box>
                        )}
                    </Paper>

                    <Paper sx={{ p: 3 }}>
                        <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
                            <Tab label={t("documents.documentDetails")} />
                            <Tab label={t("documents.documentHistory")} />
                        </Tabs>

                        {tabIndex === 0 && (
                            <Box>
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                            {t("documents.identification")}
                                        </Typography>
                                        <InfoField label={t("documents.identifier")} value={document.identificador} />
                                        <InfoField label={t("documents.documentType")} value={document.tipoDocumento} />
                                        <InfoField label={t("common.title")} value={document.titulo} />
                                        <InfoField label={t("common.situation")} value={document.situacao} />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                            {t("documents.authorshipAndDates")}
                                        </Typography>
                                        <InfoField label={t("documents.author")} value={document.autor} />
                                        <InfoField label={t("common.creationDate")} value={formatDate(document.dataCriacao)} />
                                        <InfoField label={t("documents.registrationDate")} value={formatDate(document.dataRegistro)} />
                                        <InfoField label={t("documents.support")} value={document.suporte} />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                            {t("documents.originAndStorage")}
                                        </Typography>
                                        <InfoField label={t("documents.sourceSystem")} value={document.sistemaOrigem} />
                                        <InfoField label={t("documents.sourceProcess")} value={document.processoOrigem} />
                                        <InfoField label={t("documents.folder")} value={document.pasta} />
                                        <InfoField label={t("documents.personalData")} value={document.possuiDadosPessoais ? t("common.yes") : t("common.no")} />
                                        <InfoField label={t("documents.sensitiveData")} value={document.possuiDadosSensiveis ? t("common.yes") : t("common.no")} />
                                    </Grid>
                                </Grid>

                                {metadataKeys.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                            {t("documents.metadata")}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {metadataKeys.map((key) => (
                                                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                                                    <InfoField label={key} value={metadados[key]} />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                            </Box>
                        )}

                        {tabIndex === 1 && document.idInterno && (
                            <Box sx={{ mt: 2 }}>
                                <DocumentHistoryTab tenantId={tenant} documentId={document.idInterno} />
                            </Box>
                        )}
                    </Paper>

                    {
                        document.erros && document.erros.length > 0 && (
                            <Alert severity="warning">
                                {document.erros.join(", ")}
                            </Alert>
                        )
                    }
                </>
            )
            }
        </Stack >
    );
}
